import { defineCopy } from '@floorfile/i18n'

/**
 * The application shell's catalogue — chrome, not content.
 *
 * Namespace `app`, which is the directory name (`src/app`). The locale loader
 * derives namespaces from the path, so the two must agree;
 * `copy-namespaces.test.ts` asserts it.
 *
 * ## The `nav` group is keyed by ROUTE PATH, not by a dot-path
 *
 * A documented deviation from D-L3 ("keys are namespaced dot-paths, never
 * English"), and only from its letter. Each of the 36 feature slices registers
 * its own sidebar entry from its `nav.ts` with an English `label`; the shell
 * has never held that list and must not start, because a shell that knows
 * feature names is a shell every new feature has to edit (OVERVIEW §3).
 *
 * A route path is already the nav registry's primary key, is already central in
 * `nav-registry.ts`'s `GROUP_BY_PATH`, is stable across renames, and — crucially
 * — is not English. It satisfies exactly what D-L3 is protecting against: no
 * two entries collide because two features happened to pick the same word.
 * The feature's `label` remains the English source of truth and the fallback,
 * so an unlisted entry renders its own English rather than a key.
 */
export const appCopy = defineCopy('app', {
  nav: {
    pinned: 'Pinned',
    pin: 'Pin {label}',
    unpin: 'Unpin {label}',
    groups: {
      'my-tasks': 'My Tasks',
      quality: 'Quality',
      maintenance: 'Maintenance',
      hr: 'Human Resources',
      facilities: 'Facilities',
      metrics: 'Metrics',
      settings: 'Settings',
    },
    // Keyed by route path — see the header. Ordered as the sidebar orders them.
    items: {
      '/': 'Home',
      '/checks': 'Checks',
      '/cars': 'CARs',
      '/audits': 'Audits',
      '/inspections': 'Inspections',
      // 08-12 audit E2: the one mixed console became three workspaces plus a
      // personal queue. '/assignment' is gone from this catalogue because no
      // feature registers it any more — its ROUTE survives as a redirect, and
      // a key nobody reads is the exact defect nav-copy-keys.test.ts exists
      // to catch.
      '/my-assignments': 'My assignments',
      '/my-work': 'Available work',
      '/checks/assignment': 'Check assignment',
      /** FF-AUDIT-20260914-210459-022: the sidebar is 156 px of text; these
       * three were clipped to "Maintenance dispat…". The page keeps the long
       * title; the nav label is the short one, and every nav link now carries
       * a tooltip with the full label as the belt. */
      '/maintenance/dispatch': 'Dispatch',
      '/assignment/activity': 'Assignment activity',
      '/help-requests': 'Help requests',
      '/quality/changes': 'Change control',
      '/quality/deviations': 'Deviations',
      '/quality/oos': 'OOS investigations',
      '/quality/lots': 'Lot traceability',
      '/quality/nonconformances': 'Quality issues (NCRs)',
      '/quality/esign': 'E-signatures',
      '/quality/fmea': 'Failure risk analysis',
      '/quality/risks': 'Risk register',
      '/suppliers/quality': 'Supplier quality',
      '/equipment': 'Equipment',
      '/maintenance': 'Work orders',
      '/downtime': 'Downtime',
      '/telemetry-rules': 'Sensor rules',
      '/parts': 'Parts',
      '/vendors': 'Vendors',
      '/safety': 'Safety',
      '/training': 'Training',
      '/workers': 'Workers',
      '/attendance': 'Attendance',
      '/roster': 'Shift schedule',
      '/people/hr-records': 'HR records',
      '/locations': 'Locations',
      '/organization': 'Organization',
      '/documents': 'Documents',
      '/forms': 'Forms',
      '/chat': 'Chat',
      '/inbox': 'Inbox',
      '/notes': 'Notes',
      '/reports': 'Reports',
      '/ai': 'AI',
      '/integrations': 'Integrations',
      '/automation': 'Automation',
      '/settings': 'Settings',
      '/settings/change-control': 'Change approvals',
      '/settings/governance': 'Data retention and legal holds',
      '/settings/modules': 'Modules',
      '/settings/search': 'Search settings',
    },
  },

  shell: {
    /** The product name. In the catalogue not because it needs translating —
     * it is a proper noun — but because a brand name repeated in JSX is a
     * rebrand that has to be grepped for, and because the copy guard is right
     * to flag any bare word in JSX rather than keep a list of the ones that are
     * secretly fine. */
    brand: 'FloorFile',
    sidebar: 'Sidebar',
    navigation: 'Navigation',
    goToDashboard: 'Go to dashboard',
    companyLogo: 'Company logo',
    toggleSidebar: 'Toggle sidebar',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    toggleTheme: 'Toggle theme',
    accountMenu: 'Account menu',
    closeAccountMenu: 'Close account menu',
  },

  account: {
    settings: 'Account settings',
    offlineSecurity: 'Offline security',
    switchUser: 'Switch user',
    signOut: 'Sign out',
    /** Density (Fiix audit §8.2) — a three-way choice, remembered per device. */
    density: 'Density',
    densityAuto: 'Automatic',
    densityComfortable: 'Comfortable',
    densityCompact: 'Compact',
  },

  banner: {
    // `<0>` wraps only the emphasised lead, so the sentence stays one
    // translatable unit instead of two fragments whose word order French may
    // not allow.
    dbOutage:
      '<0>Server temporarily unavailable.</0> You are seeing cached data; new changes are saved locally and sync when the server recovers.',
    impersonation: 'Read-only platform view',
    returnToAdmin: 'Return to platform admin',
    offlineSignedIn: 'Signed in offline as {name} — sign in to sync your queued work.',
    signInToSync: 'Sign in to sync',
  },

  gate: {
    loadingCompanyAccess: 'Loading company access…',
    loadingModuleAccess: 'Loading module access…',
    noAccessTitle: 'No access',
    // Verbatim, apostrophe included: `assignment-governance.spec.ts` asserts
    // `/no access|don't have access/i` has zero matches for an owner. Phase 3
    // moves strings; it does not edit them.
    noAccessArea: "You don't have access to this area.",
    featureUnavailable: 'Feature unavailable',
    chatDisabled: 'Chat is disabled for your company.',
    aiDisabled: 'AI analytics is disabled for your company.',
    moduleUnavailableTitle: 'Module unavailable',
    moduleUnavailableBody: 'This capability is disabled for your company.',
    openModuleSettings: 'Open module settings',
  },

  /** The route-level error boundary. Two cases, deliberately worded apart: a
   * crash is a fault and a not-yet-downloaded screen is a known limitation of
   * the offline-first allow-list (`vite.config.ts`), and telling someone with
   * no signal that "something went wrong" sends them looking for a bug that
   * is not there. */
  routeError: {
    title: 'Something went wrong loading this page',
    body: 'A network hiccup or an app update may have interrupted the load.',
    reload: 'Reload',
    offlineTitle: 'This screen is not available offline yet',
    offlineBody:
      'It has not been downloaded to this device. Open it once with a connection and it will work offline from then on.',
  },

  /**
   * A URL that matches no route.
   *
   * Deliberately worded apart from `routeError`, which it used to fall into.
   * "Something went wrong loading this page" plus a `Reload` button says the
   * app broke and invites the user to retry — for a mistyped or stale address,
   * both are false, and reloading does exactly nothing. This says what
   * happened and offers the two things that actually help.
   */
  notFound: {
    title: 'That page does not exist',
    body: 'The address may be mistyped, or the link that brought you here may be out of date.',
    home: 'Go to the dashboard',
    back: 'Go back',
  },

  common: {
    loading: 'Loading…',
    moreActions: 'More actions',
    search: 'Search',
  },

  staleUpdate: {
    title: 'This record changed',
    description: 'Someone saved a newer version while you were editing. Your unsaved fields remain on this screen until you choose to reload.',
    modified: 'The record was modified by someone else.',
    latestVersion: 'The latest server version is {version}.',
    keepDraft: 'Keep my draft',
    reloadLatest: 'Reload latest',
  },

  authCommon: {
    email: 'Email',
    password: 'Password',
    authenticationCode: 'Authentication code',
    sixDigitCode: '6-digit code',
    signOut: 'Sign out',
    goToSignIn: 'Go to sign in',
    backToSignIn: 'Back to sign in',
    codeNotAccepted: 'Code not accepted.',
    couldNotStartTwoFactor: 'Could not start 2FA setup.',
    pinNotAccepted: 'PIN not accepted.',
    pinsDoNotMatch: 'PINs do not match.',
    requestCouldNotComplete: 'The request could not be completed.',
  },

  login: {
    mfaPrompt: 'Enter the 6-digit code from your authenticator app.',
    prompt: 'Sign in to your plant.',
    forgotPassword: 'Forgot password?',
    failed: 'Sign-in failed.',
    signingIn: 'Signing in…',
    verifyCode: 'Verify code',
    signIn: 'Sign in',
    newPlant: 'New plant?',
    createCompany: 'Create your company',
  },

  emailVerification: {
    gateTitle: 'Verify your work email',
    gateDescription: 'We sent a 24-hour verification link to <0>{email}</0>. FloorFile keeps this account permission-free until that link is used.',
    resendSuccess: 'If verification is still pending, a fresh link is on the way.',
    invitedGateTitle: 'Finish setting up your account',
    invitedGateDescription: 'The account for <0>{email}</0> has not been activated yet. Use the invitation link your administrator sent you, or send yourself a fresh one below.',
    invitedResend: 'Send me a new invitation link',
    invitedResendSuccess: 'If this account is still waiting to be activated, a fresh invitation link is on the way.',
    resendError: 'Could not resend the email.',
    sending: 'Sending…',
    resend: 'Resend verification email',
    pageTitle: 'Email verification',
    verifying: 'Verifying your email…',
    missingToken: 'The verification link is missing its token.',
    verified: 'Your email is verified.',
    invalid: 'The verification link is invalid or expired.',
    continueToSignIn: 'Continue to sign in',
    returnToSignIn: 'Return to sign in',
  },

  twoFactorEnrollment: {
    title: 'Set up two-factor authentication',
    required: '{email} — your company requires two-factor authentication before you can continue.',
    enabled: 'Two-factor authentication is on. Store these one-time backup codes somewhere safe — they are shown only once:',
    signingIn: 'Signing you in…',
    continue: 'Continue to FloorFile',
    instructions: 'Add this account to your authenticator app, then enter a code to finish:',
    manualKey: 'Manual entry key:',
    checking: 'Checking…',
    enable: 'Turn on 2FA',
    preparing: 'Preparing setup…',
  },

  offlineUnlock: {
    unlockTitle: 'Unlock offline access',
    setupTitle: 'Set up offline encryption',
    accountFallback: 'Your account',
    unlockDescription: '{account} — enter your device PIN to unlock encrypted data stored on this device.',
    setupDescription: '{account} — your company requires offline encryption. Set a PIN to protect the data this device stores for you.',
    setupError: 'Could not set up offline encryption.',
    pin: 'PIN',
    unlocking: 'Unlocking…',
    unlock: 'Unlock',
    /** FF-AUDIT-098: "Sign out" beside the PIN field said nothing about what it costs. */
    signOutHint: 'Signing out keeps the encrypted data on this device; you will need your password to sign back in and this PIN to read it.',
    choosePin: 'Choose a 4–12 digit PIN. It cannot be recovered if forgotten — a forgotten PIN means setting a new one and losing access to any data encrypted under the old one.',
    newPin: 'New PIN',
    confirmPin: 'Confirm PIN',
    settingUp: 'Setting up…',
    setPin: 'Set PIN',
  },

  passwordReset: {
    sent: 'If an account exists for <0>{email}</0>, a reset link is on its way. The link is valid for 1 hour.',
    instructions: "Enter your work email and we'll send a password-reset link.",
    sending: 'Sending…',
    sendLink: 'Send reset link',
    mismatch: 'Passwords do not match.',
    invalid: 'Reset link is invalid or expired.',
    invitePending: 'This account has not been activated yet. We have sent a fresh invitation link to your email — use it to finish setting up.',
    couldNotReset: 'Could not reset the password.',
    missingToken: 'This reset link is missing its token.',
    requestNew: 'Request a new link',
    updated: 'Password updated — sign in with your new password.',
    choose: 'Choose a new password (12+ characters).',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    saving: 'Saving…',
    setNewPassword: 'Set new password',
  },

  registration: {
    inviteOnly: 'FloorFile is currently in invite-only early access. New plants are set up personally — if you have an invite, use the sign-in link your administrator sent you.',
    prompt: "Create your plant. You'll be the owner account.",
    companyName: 'Company name',
    firstName: 'First name',
    lastName: 'Last name',
    workEmail: 'Work email',
    passwordHint: 'Use 12+ characters and several unrelated words. Common or breached passwords are refused.',
    confirmPassword: 'Confirm password',
    mismatch: 'Passwords do not match.',
    tooShort: 'Password must be at least 12 characters.',
    failed: 'Registration failed.',
    creating: 'Creating…',
    createCompany: 'Create company',
    existingAccount: 'Already have an account?',
    signIn: 'Sign in',
  },

  acceptInvite: {
    title: 'Accept your invitation',
    prompt: 'Finish setting up your FloorFile account — tell us your name and choose a password.',
    accept: 'Accept invitation',
    accepting: 'Joining…',
    missingToken: 'This invitation link is missing its token. Open the link from your invitation email, or ask your administrator to send a new one.',
    invalid: 'This invitation is invalid or has expired. Ask your administrator to send a new one.',
    noLongerValid: 'This invitation is no longer valid. Ask your administrator to send a new one.',
    failed: 'Could not accept the invitation.',
  },

  accountPage: {
    title: 'Account',
    description: 'Your personal sign-in and security settings.',
    profileHeading: 'Profile & preferences',
    /** Owner decision 2026-09-28: every role's own notification preferences and export files live here, not under Settings. */
    notificationsHeading: 'Notifications',
    exportsHeading: 'My exports',
    loadingProfile: 'Loading profile…',
    avatar: 'Your avatar',
    replaceAvatar: 'Replace avatar',
    uploadAvatar: 'Upload avatar',
    firstName: 'First name',
    lastName: 'Last name',
    theme: 'Theme',
    themeOption: { system: 'System', light: 'Light', dark: 'Dark' },
    profileSaved: 'Profile saved.',
    saving: 'Saving…',
    saveProfile: 'Save profile',
    password: {
      heading: 'Change password',
      current: 'Current password',
      new: 'New password (12+ characters)',
      confirm: 'Confirm new password',
      mismatch: 'New passwords do not match.',
      failed: 'Could not change the password.',
      changed: 'Password changed. Your other devices were signed out.',
      changing: 'Changing…',
      submit: 'Change password',
    },
    twoFactor: {
      heading: 'Two-factor authentication',
      saveCodes: 'Save these backup codes now — they are shown only once:',
      enabled: '2FA is enabled on your account.',
      authenticatorCode: 'Authenticator code',
      working: 'Working…',
      disable: 'Disable 2FA',
      instructions: 'Add this account to your authenticator app, then enter a code to finish:',
      manualKey: 'Manual entry key:',
      verifying: 'Verifying…',
      enable: 'Enable 2FA',
      protection: 'Protect your account with one-time codes from an authenticator app.',
      starting: 'Starting…',
      setup: 'Set up 2FA',
      disableFailed: 'Could not disable 2FA.',
    },
    sessions: {
      heading: 'Active sessions',
      loadFailed: 'Could not load sessions.',
      revokeFailed: 'Could not revoke that session.',
      thisDevice: 'This device',
      unknownIp: 'unknown IP',
      active: 'active {date}',
      signOut: 'Sign out',
      older: { one: '…and {count} older session. "Sign out everywhere" revokes it.', other: '…and {count} older sessions. "Sign out everywhere" revokes them all.' },
      signOutEverywhere: 'Sign out everywhere',
      signOutEverywhereTitle: 'Sign out on every device?',
      signOutEverywhereBody: 'You will be signed out here and on every other phone, tablet and computer where you are signed in. Work queued offline on those devices is kept until you sign in there again.',
      browser: {
        unknown: 'Unknown device',
        edge: 'Edge',
        chrome: 'Chrome',
        firefox: 'Firefox',
        safari: 'Safari',
      },
    },
  },

  offlineSecurity: {
    title: 'Offline security',
    description: 'Encrypts the data this device stores for you at rest, behind a device PIN.',
    statusLabel: 'Status:',
    on: 'On',
    off: 'Off',
    choosePin: 'Choose a 4–12 digit PIN. It cannot be recovered if forgotten.',
    newPin: 'New PIN',
    confirmPin: 'Confirm PIN',
    currentPin: 'Current PIN',
    confirmNewPin: 'Confirm new PIN',
    enable: 'Enable offline encryption',
    enabling: 'Enabling…',
    enabled: 'Offline encryption is on for this device.',
    changePinHeading: 'Change PIN',
    /** FF-AUDIT-20260914-210459-098: the heading says "Change PIN"; the button says what pressing it does. */
    changePin: 'Save new PIN',
    changing: 'Changing…',
    pinChanged: 'PIN changed.',
    /** FF-AUDIT-098: sits beside "Disable offline signing" — say which one. */
    disable: 'Disable offline encryption',
    disabling: 'Disabling…',
    confirmDisable: 'Confirm disable',
    disabled: 'Offline encryption is off on this device.',
    required: 'Required by your company — this cannot be turned off.',
    disableWarning:
      'This removes your PIN-wrapped key on this device. Cached encrypted data becomes unreadable here until you enable it again.',
    cancel: 'Cancel',
    /** FF-AUDIT-098: a way out that is not "Disable", and the launch rule said out loud. */
    done: 'Done',
    askedEveryLaunch: 'This device asks for the PIN each time the app starts, online or offline — the key that unlocks your stored data lives only behind it.',
    pinsDoNotMatch: 'PINs do not match.',
    newPinsDoNotMatch: 'New PINs do not match.',
    couldNotEnable: 'Could not enable offline encryption.',
    couldNotChange: 'Could not change the PIN.',
    couldNotDisable: 'Could not disable offline encryption.',
  },

  /** FF-AUDIT-089 — offline Part 11 signing, a section of the Offline security dialog. */
  offlineSigning: {
    heading: 'Offline signing (Part 11)',
    description:
      'Lets you sign actions that need an electronic signature — such as completing a check — with your password while this device is offline. The signature is applied to the record as soon as the device reconnects. Turning this on re-authenticates you now and stores a signing credential on this device, locked with your password; it expires after 30 days and is revoked if your password changes.',
    statusLabel: 'Status:',
    onUntil: 'On until {when}',
    off: 'Off',
    password: 'Password',
    authenticatorCode: 'Authenticator code',
    enable: 'Enable offline signing',
    enabling: 'Enabling…',
    enabled: 'Offline signing is on for this device.',
    disable: 'Disable offline signing',
    disabling: 'Disabling…',
    disabled: 'Offline signing is off on this device.',
    couldNotEnable: 'Could not enable offline signing.',
    couldNotDisable: 'Could not disable offline signing.',
  },

  /**
   * The register sort announcement — chrome, not any one feature's words.
   *
   * `sortAnnouncement()` in `lib/table-sort.ts` composed
   * `Sorted by ${label}, ${asc ? 'ascending' : 'descending'}` around a label
   * the CALLER supplies. Migrating a slice therefore translated the fragment
   * and left the sentence English: French registers read
   * "Sorted by ouverture, descending". 37 call sites across 18 files, so the
   * sentence moved here rather than into a parameter on every one of them.
   */
  sort: {
    ascending: 'Sorted by {field}, ascending',
    descending: 'Sorted by {field}, descending',
  },

  switchUser: {
    title: 'Switch user',
    description: 'Switch to another user enrolled for offline access on this device.',
    loading: 'Loading…',
    enterPinFor: 'Enter the PIN for <0>{name}</0> ({email}).',
    pin: 'PIN',
    submit: 'Switch',
    switching: 'Switching…',
    back: 'Back',
    none: 'No other users have enabled offline access on this device yet. They can turn it on from their own account menu after signing in here.',
    addAnother: 'Add another user…',
    pinNotAccepted: 'PIN not accepted.',
  },
})

export type AppCopy = typeof appCopy.en
